// Popular cities per country, for TripAI's "Explore Destinations" feature —
// lets a user who only knows a country (not specific cities) get a
// reasonable auto-populated destination list. lat/lon are stored directly so
// Explore Destinations never needs a geocode call for a picked city.
//
// Being converted region-by-region from the user's real dataset
// (SANDISK-512/Guide Data/Guides/*.xlsx) — Europe done (2026-08-24, from
// Europe1.xlsx), other regions still the original hand-written stub below
// until their turn. Source spreadsheet has no lat/lon; every coordinate here
// is a best-effort estimate, not a geocoded value. Source also has no
// explicit popularity score — an "x" marker (per the user, likely "this city
// already has a featured guide in the app" rather than a measured popularity
// rank) identifies each country's anchor cities, and a "Days" column (a
// rough recommended-visit-length hint on some non-anchor cities) is used to
// scale the rest — a bigger recommended stay implies a more substantial
// destination. Cities with neither signal were mostly omitted per-country
// (the source often lists 20-45 minor/resort towns per country) in favor of
// a manageable curated set, per the "20% of cities cover 80% of a country's
// destinations" framing this feature was scoped around.
// Keyed by the exact country name strings used in Countries.js's `countries`
// export (not the alpha-2 codes in `countries_continents`).
export const citiesByCountry = {
  "France": [
    { city: "Paris", lat: 48.8566, lon: 2.3522, popularity: 100 },
    { city: "Nice", lat: 43.7102, lon: 7.2620, popularity: 95 },
    { city: "Lyon", lat: 45.7640, lon: 4.8357, popularity: 85 },
    { city: "Marseille", lat: 43.2965, lon: 5.3698, popularity: 83 },
    { city: "Ajaccio", lat: 41.9192, lon: 8.7386, popularity: 80 },
    { city: "Bordeaux", lat: 44.8378, lon: -0.5792, popularity: 72 },
    { city: "Strasbourg", lat: 48.5734, lon: 7.7521, popularity: 60 },
    { city: "Reims", lat: 49.2583, lon: 4.0317, popularity: 58 },
    { city: "Avignon", lat: 43.9493, lon: 4.8055, popularity: 55 },
    { city: "Cannes", lat: 43.5528, lon: 7.0174, popularity: 55 },
    { city: "Antibes", lat: 43.5808, lon: 7.1251, popularity: 50 },
    { city: "Colmar", lat: 48.0794, lon: 7.3585, popularity: 48 },
    { city: "Mont Saint-Michel", lat: 48.6361, lon: -1.5115, popularity: 45 },
    { city: "Tours", lat: 47.3941, lon: 0.6848, popularity: 42 },
  ],
  "Spain": [
    { city: "Barcelona", lat: 41.3874, lon: 2.1686, popularity: 100 },
    { city: "Madrid", lat: 40.4168, lon: -3.7038, popularity: 95 },
    { city: "Ibiza", lat: 38.9067, lon: 1.4206, popularity: 80 },
    { city: "Mallorca", lat: 39.6953, lon: 3.0176, popularity: 78 },
    { city: "Benidorm", lat: 38.5411, lon: -0.1225, popularity: 78 },
    { city: "Valencia", lat: 39.4699, lon: -0.3763, popularity: 75 },
    { city: "Seville", lat: 37.3891, lon: -5.9845, popularity: 72 },
    { city: "Toledo", lat: 39.8628, lon: -4.0273, popularity: 68 },
    { city: "Bilbao", lat: 43.2630, lon: -2.9350, popularity: 68 },
    { city: "San Sebastian", lat: 43.3183, lon: -1.9812, popularity: 60 },
    { city: "Alicante", lat: 38.3452, lon: -0.4810, popularity: 58 },
    { city: "Granada", lat: 37.1773, lon: -3.5986, popularity: 55 },
    { city: "Marbella", lat: 36.5099, lon: -4.8850, popularity: 52 },
    { city: "Ronda", lat: 36.7461, lon: -5.1633, popularity: 40 },
  ],
  "Italy": [
    { city: "Rome", lat: 41.9028, lon: 12.4964, popularity: 100 },
    { city: "Venice", lat: 45.4408, lon: 12.3155, popularity: 95 },
    { city: "Milan", lat: 45.4642, lon: 9.1900, popularity: 92 },
    { city: "Florence", lat: 43.7696, lon: 11.2558, popularity: 90 },
    { city: "Naples", lat: 40.8518, lon: 14.2681, popularity: 85 },
    { city: "Palermo", lat: 38.1157, lon: 13.3615, popularity: 78 },
    { city: "Turin", lat: 45.0703, lon: 7.6869, popularity: 70 },
    { city: "Padua", lat: 45.4064, lon: 11.8768, popularity: 70 },
    { city: "Verona", lat: 45.4384, lon: 10.9916, popularity: 68 },
    { city: "Bologna", lat: 44.4949, lon: 11.3426, popularity: 65 },
    { city: "Genoa", lat: 44.4056, lon: 8.9463, popularity: 65 },
    { city: "Pisa", lat: 43.7228, lon: 10.4017, popularity: 58 },
    { city: "Sorrento", lat: 40.6263, lon: 14.3758, popularity: 55 },
    { city: "Assisi", lat: 43.0707, lon: 12.6196, popularity: 55 },
    { city: "Ischia", lat: 40.7357, lon: 13.8951, popularity: 45 },
    { city: "Ravenna", lat: 44.4184, lon: 12.2035, popularity: 40 },
  ],
  "Turkey": [
    { city: "Istanbul", lat: 41.0082, lon: 28.9784, popularity: 100 },
    { city: "Antalya", lat: 36.8969, lon: 30.7133, popularity: 85 },
    { city: "Muğla", lat: 37.2153, lon: 28.3636, popularity: 70 },
    { city: "Cappadocia", lat: 38.6310, lon: 34.9130, popularity: 65 },
    { city: "Bodrum", lat: 37.0344, lon: 27.4305, popularity: 65 },
    { city: "Izmir", lat: 38.4237, lon: 27.1428, popularity: 60 },
    { city: "Fethiye", lat: 36.6217, lon: 29.1164, popularity: 55 },
    { city: "Kuşadası", lat: 37.8579, lon: 27.2610, popularity: 55 },
    { city: "Ankara", lat: 39.9334, lon: 32.8597, popularity: 55 },
    { city: "Trabzon", lat: 41.0027, lon: 39.7168, popularity: 40 },
    { city: "Safranbolu", lat: 41.2506, lon: 32.6942, popularity: 38 },
  ],
  "Germany": [
    { city: "Berlin", lat: 52.5200, lon: 13.4050, popularity: 100 },
    { city: "Munich", lat: 48.1351, lon: 11.5820, popularity: 95 },
    { city: "Hamburg", lat: 53.5511, lon: 9.9937, popularity: 88 },
    { city: "Frankfurt", lat: 50.1109, lon: 8.6821, popularity: 80 },
    { city: "Dresden", lat: 51.0504, lon: 13.7373, popularity: 78 },
    { city: "Stuttgart", lat: 48.7758, lon: 9.1829, popularity: 78 },
    { city: "Düsseldorf", lat: 51.2277, lon: 6.7735, popularity: 65 },
    { city: "Potsdam", lat: 52.3906, lon: 13.0645, popularity: 65 },
    { city: "Nuremberg", lat: 49.4521, lon: 11.0767, popularity: 62 },
    { city: "Leipzig", lat: 51.3397, lon: 12.3731, popularity: 62 },
    { city: "Bonn", lat: 50.7374, lon: 7.0982, popularity: 58 },
    { city: "Hannover", lat: 52.3759, lon: 9.7320, popularity: 58 },
    { city: "Füssen", lat: 47.5576, lon: 10.7498, popularity: 55 },
    { city: "Heidelberg", lat: 49.3988, lon: 8.6724, popularity: 48 },
  ],
  "United Kingdom": [
    { city: "London", lat: 51.5074, lon: -0.1278, popularity: 100 },
    { city: "Edinburgh", lat: 55.9533, lon: -3.1883, popularity: 85 },
    { city: "Bath", lat: 51.3811, lon: -2.3590, popularity: 75 },
    { city: "Manchester", lat: 53.4808, lon: -2.2426, popularity: 68 },
    { city: "Glasgow", lat: 55.8642, lon: -4.2518, popularity: 65 },
    { city: "York", lat: 53.9600, lon: -1.0873, popularity: 72 },
    { city: "Liverpool", lat: 53.4084, lon: -2.9916, popularity: 65 },
    { city: "Cambridge", lat: 52.2053, lon: 0.1218, popularity: 70 },
    { city: "Oxford", lat: 51.7520, lon: -1.2577, popularity: 70 },
    { city: "Cardiff", lat: 51.4816, lon: -3.1791, popularity: 60 },
    { city: "Belfast", lat: 54.5973, lon: -5.9301, popularity: 58 },
    { city: "Inverness", lat: 57.4778, lon: -4.2247, popularity: 55 },
    { city: "Bourton-on-the-Water", lat: 51.8767, lon: -1.7502, popularity: 45 },
  ],
  "Austria": [
    { city: "Vienna", lat: 48.2082, lon: 16.3738, popularity: 100 },
    { city: "Salzburg", lat: 47.8095, lon: 13.0550, popularity: 78 },
    { city: "Innsbruck", lat: 47.2692, lon: 11.4041, popularity: 72 },
    { city: "Graz", lat: 47.0707, lon: 15.4395, popularity: 55 },
    { city: "Linz", lat: 48.3069, lon: 14.2858, popularity: 52 },
  ],
  "Greece": [
    { city: "Heraklion", lat: 35.3387, lon: 25.1442, popularity: 90 },
    { city: "Rhodes", lat: 36.4341, lon: 28.2176, popularity: 88 },
    { city: "Santorini", lat: 36.3932, lon: 25.4615, popularity: 100 },
    { city: "Chania", lat: 35.5138, lon: 24.0180, popularity: 80 },
    { city: "Mykonos", lat: 37.4467, lon: 25.3289, popularity: 85 },
    { city: "Thessaloniki", lat: 40.6401, lon: 22.9444, popularity: 70 },
    { city: "Corfu", lat: 39.6243, lon: 19.9217, popularity: 65 },
    { city: "Meteora", lat: 39.7217, lon: 21.6304, popularity: 48 },
    { city: "Delphi", lat: 38.4824, lon: 22.5010, popularity: 42 },
    { city: "Nafplio", lat: 37.5686, lon: 22.8078, popularity: 38 },
  ],
  "Portugal": [
    { city: "Lisbon", lat: 38.7223, lon: -9.1393, popularity: 100 },
    { city: "Porto", lat: 41.1579, lon: -8.6291, popularity: 85 },
    { city: "Funchal", lat: 32.6669, lon: -16.9241, popularity: 65 },
    { city: "Lagos", lat: 37.1021, lon: -8.6743, popularity: 55 },
    { city: "Sintra", lat: 38.8029, lon: -9.3817, popularity: 60 },
    { city: "Coimbra", lat: 40.2033, lon: -8.4103, popularity: 55 },
    { city: "Braga", lat: 41.5454, lon: -8.4265, popularity: 45 },
    { city: "Évora", lat: 38.5667, lon: -7.9000, popularity: 40 },
    { city: "Aveiro", lat: 40.6443, lon: -8.6455, popularity: 35 },
  ],
  "Japan": [
    { city: "Tokyo", lat: 35.6762, lon: 139.6503, popularity: 100 },
    { city: "Kyoto", lat: 35.0116, lon: 135.7681, popularity: 92 },
    { city: "Osaka", lat: 34.6937, lon: 135.5023, popularity: 88 },
    { city: "Fukuoka", lat: 33.5904, lon: 130.4017, popularity: 65 },
    { city: "Chiba", lat: 35.6073, lon: 140.1065, popularity: 55 },
    { city: "Hiroshima", lat: 34.3853, lon: 132.4553, popularity: 70 },
    { city: "Nara", lat: 34.6851, lon: 135.8048, popularity: 65 },
    { city: "Sapporo", lat: 43.0618, lon: 141.3545, popularity: 62 },
    { city: "Hakone", lat: 35.2323, lon: 139.1069, popularity: 55 },
    { city: "Kanazawa", lat: 36.5613, lon: 136.6562, popularity: 48 },
    { city: "Nagasaki", lat: 32.7503, lon: 129.8779, popularity: 48 },
    { city: "Kobe", lat: 34.6901, lon: 135.1955, popularity: 45 },
    { city: "Nikko", lat: 36.7198, lon: 139.6982, popularity: 42 },
    { city: "Takayama", lat: 36.1461, lon: 137.2521, popularity: 38 },
  ],
  "Australia": [
    { city: "Sydney", lat: -33.8688, lon: 151.2093, popularity: 100 },
    { city: "Melbourne", lat: -37.8136, lon: 144.9631, popularity: 90 },
    { city: "Cairns", lat: -16.9186, lon: 145.7781, popularity: 65 },
    { city: "Brisbane", lat: -27.4698, lon: 153.0251, popularity: 65 },
    { city: "Perth", lat: -31.9505, lon: 115.8605, popularity: 60 },
    { city: "Gold Coast", lat: -28.0167, lon: 153.4000, popularity: 58 },
    { city: "Adelaide", lat: -34.9285, lon: 138.6007, popularity: 50 },
    { city: "Canberra", lat: -35.2809, lon: 149.1300, popularity: 45 },
    { city: "Hobart", lat: -42.8821, lon: 147.3272, popularity: 42 },
    { city: "Alice Springs", lat: -23.6980, lon: 133.8807, popularity: 42 },
    { city: "Darwin", lat: -12.4634, lon: 130.8456, popularity: 38 },
  ],
  "Macau": [{ city: "Macau", lat: 22.1987, lon: 113.5439, popularity: 100 }],
  "Hong Kong": [{ city: "Hong Kong", lat: 22.3193, lon: 114.1694, popularity: 100 }],
  "China": [
    { city: "Beijing", lat: 39.9042, lon: 116.4074, popularity: 100 },
    { city: "Shanghai", lat: 31.2304, lon: 121.4737, popularity: 98 },
    { city: "Xi'an", lat: 34.3416, lon: 108.9398, popularity: 75 },
    { city: "Guangzhou", lat: 23.1291, lon: 113.2644, popularity: 75 },
    { city: "Guilin", lat: 25.2736, lon: 110.2900, popularity: 70 },
    { city: "Chengdu", lat: 30.5728, lon: 104.0668, popularity: 70 },
    { city: "Shenzhen", lat: 22.5431, lon: 114.0579, popularity: 68 },
    { city: "Hangzhou", lat: 30.2741, lon: 120.1551, popularity: 65 },
    { city: "Zhangjiajie", lat: 29.1170, lon: 110.4794, popularity: 58 },
    { city: "Lhasa", lat: 29.6500, lon: 91.1000, popularity: 55 },
    { city: "Xiamen", lat: 24.4798, lon: 118.0894, popularity: 52 },
    { city: "Suzhou", lat: 31.2989, lon: 120.5853, popularity: 48 },
    { city: "Qingdao", lat: 36.0671, lon: 120.3826, popularity: 45 },
    { city: "Dalian", lat: 38.9140, lon: 121.6147, popularity: 42 },
    { city: "Tianjin", lat: 39.3434, lon: 117.3616, popularity: 40 },
    { city: "Lijiang", lat: 26.8721, lon: 100.2299, popularity: 40 },
    { city: "Dunhuang", lat: 40.1421, lon: 94.6619, popularity: 35 },
    { city: "Zhuhai", lat: 22.2707, lon: 113.5767, popularity: 32 },
  ],
  "India": [
    { city: "Delhi", lat: 28.6139, lon: 77.2090, popularity: 100 },
    { city: "Mumbai", lat: 19.0760, lon: 72.8777, popularity: 95 },
    { city: "Agra", lat: 27.1767, lon: 78.0081, popularity: 90 },
    { city: "Jaipur", lat: 26.9124, lon: 75.7873, popularity: 80 },
    { city: "Varanasi", lat: 25.3176, lon: 82.9739, popularity: 65 },
    { city: "Bangalore", lat: 12.9716, lon: 77.5946, popularity: 65 },
    { city: "Udaipur", lat: 24.5854, lon: 73.7125, popularity: 55 },
    { city: "Chennai", lat: 13.0827, lon: 80.2707, popularity: 60 },
    { city: "Kolkata", lat: 22.5726, lon: 88.3639, popularity: 58 },
    { city: "Amritsar", lat: 31.6340, lon: 74.8723, popularity: 52 },
    { city: "Kochi", lat: 9.9312, lon: 76.2673, popularity: 50 },
    { city: "Hyderabad", lat: 17.3850, lon: 78.4867, popularity: 45 },
    { city: "Pune", lat: 18.5204, lon: 73.8567, popularity: 45 },
    { city: "Darjeeling", lat: 27.0410, lon: 88.2663, popularity: 40 },
    { city: "Shimla", lat: 31.1048, lon: 77.1734, popularity: 38 },
    { city: "Madurai", lat: 9.9252, lon: 78.1198, popularity: 35 },
  ],
  "Vietnam": [
    { city: "Ho Chi Minh City", lat: 10.8231, lon: 106.6297, popularity: 100 },
    { city: "Hanoi", lat: 21.0278, lon: 105.8342, popularity: 95 },
    { city: "Ha Long", lat: 20.9101, lon: 107.1839, popularity: 80 },
    { city: "Hoi An", lat: 15.8801, lon: 108.3380, popularity: 70 },
    { city: "Da Nang", lat: 16.0544, lon: 108.2022, popularity: 68 },
    { city: "Hue", lat: 16.4637, lon: 107.5909, popularity: 55 },
    { city: "Nha Trang", lat: 12.2388, lon: 109.1967, popularity: 55 },
    { city: "Ninh Binh", lat: 20.2506, lon: 105.9744, popularity: 48 },
    { city: "Dalat", lat: 11.9404, lon: 108.4583, popularity: 45 },
    { city: "Can Tho", lat: 10.0452, lon: 105.7469, popularity: 38 },
  ],
  "Malaysia": [
    { city: "Kuala Lumpur", lat: 3.1390, lon: 101.6869, popularity: 100 },
    { city: "Penang Island", lat: 5.4141, lon: 100.3288, popularity: 78 },
    { city: "Malacca", lat: 2.1896, lon: 102.2501, popularity: 60 },
    { city: "Kota Kinabalu", lat: 5.9804, lon: 116.0735, popularity: 55 },
    { city: "Johor Bahru", lat: 1.4927, lon: 103.7414, popularity: 52 },
    { city: "Kuching", lat: 1.5535, lon: 110.3593, popularity: 48 },
    { city: "Ipoh", lat: 4.5975, lon: 101.0901, popularity: 40 },
    { city: "Putrajaya", lat: 2.9264, lon: 101.6964, popularity: 32 },
  ],
  "South Korea": [
    { city: "Seoul", lat: 37.5665, lon: 126.9780, popularity: 100 },
    { city: "Jeju", lat: 33.4996, lon: 126.5312, popularity: 75 },
    { city: "Busan", lat: 35.1796, lon: 129.0756, popularity: 68 },
    { city: "Gyeongju", lat: 35.8562, lon: 129.2247, popularity: 48 },
    { city: "Jeonju", lat: 35.8242, lon: 127.1480, popularity: 45 },
    { city: "Incheon", lat: 37.4563, lon: 126.7052, popularity: 42 },
    { city: "Gangneung", lat: 37.7519, lon: 128.8761, popularity: 38 },
    { city: "Daegu", lat: 35.8714, lon: 128.6014, popularity: 38 },
  ],
  "Indonesia": [
    { city: "Denpasar", lat: -8.6705, lon: 115.2126, popularity: 95 },
    { city: "Ubud", lat: -8.5069, lon: 115.2625, popularity: 90 },
    { city: "Kuta", lat: -8.7183, lon: 115.1686, popularity: 75 },
    { city: "Jakarta", lat: -6.2088, lon: 106.8456, popularity: 80 },
    { city: "Yogyakarta", lat: -7.7956, lon: 110.3695, popularity: 68 },
    { city: "Bandung", lat: -6.9175, lon: 107.6191, popularity: 50 },
    { city: "Surabaya", lat: -7.2575, lon: 112.7521, popularity: 48 },
    { city: "Batam", lat: 1.0456, lon: 104.0305, popularity: 40 },
    { city: "Medan", lat: 3.5952, lon: 98.6722, popularity: 38 },
    { city: "Makassar", lat: -5.1477, lon: 119.4327, popularity: 35 },
  ],
  "Nepal": [
    { city: "Kathmandu", lat: 27.7172, lon: 85.3240, popularity: 100 },
    { city: "Pokhara", lat: 28.2096, lon: 83.9856, popularity: 80 },
    { city: "Annapurna Circuit", lat: 28.5967, lon: 83.8203, popularity: 60 },
    { city: "Lumbini", lat: 27.4833, lon: 83.2767, popularity: 55 },
    { city: "Chitwan National Park", lat: 27.5291, lon: 84.3542, popularity: 55 },
    { city: "Bhaktapur", lat: 27.6710, lon: 85.4298, popularity: 50 },
    { city: "Patan", lat: 27.6588, lon: 85.3247, popularity: 45 },
    { city: "Nagarkot", lat: 27.7172, lon: 85.5202, popularity: 38 },
  ],
  "Jordan": [
    { city: "Petra", lat: 30.3285, lon: 35.4444, popularity: 100 },
    { city: "Amman", lat: 31.9454, lon: 35.9284, popularity: 90 },
    { city: "Wadi Rum", lat: 29.5324, lon: 35.4206, popularity: 75 },
    { city: "Dead Sea", lat: 31.5590, lon: 35.4732, popularity: 65 },
    { city: "Aqaba", lat: 29.5321, lon: 35.0063, popularity: 55 },
    { city: "Jerash", lat: 32.2811, lon: 35.8994, popularity: 50 },
    { city: "Madaba", lat: 31.7197, lon: 35.7934, popularity: 35 },
  ],
  "Philippines": [
    { city: "Boracay", lat: 11.9674, lon: 121.9248, popularity: 100 },
    { city: "Palawan", lat: 9.8349, lon: 118.7384, popularity: 95 },
    { city: "Manila", lat: 14.5995, lon: 120.9842, popularity: 90 },
    { city: "Cebu", lat: 10.3157, lon: 123.8854, popularity: 75 },
    { city: "Bohol", lat: 9.8500, lon: 124.1435, popularity: 60 },
    { city: "Tagaytay", lat: 14.1153, lon: 120.9621, popularity: 45 },
    { city: "Vigan", lat: 17.5747, lon: 120.3869, popularity: 38 },
  ],
  "Oman": [
    { city: "Muscat", lat: 23.5880, lon: 58.3829, popularity: 100 },
    { city: "Salalah", lat: 17.0151, lon: 54.0924, popularity: 60 },
    { city: "Nizwa", lat: 22.9333, lon: 57.5333, popularity: 55 },
    { city: "Wahiba Sands", lat: 22.0333, lon: 58.3333, popularity: 45 },
    { city: "Musandam Fjords", lat: 26.1967, lon: 56.2500, popularity: 42 },
    { city: "Jebel Akhdar", lat: 23.0833, lon: 57.6667, popularity: 40 },
  ],
  "Sri Lanka": [
    { city: "Sigiriya", lat: 7.9570, lon: 80.7603, popularity: 90 },
    { city: "Colombo", lat: 6.9271, lon: 79.8612, popularity: 80 },
    { city: "Kandy", lat: 7.2906, lon: 80.6337, popularity: 75 },
    { city: "Galle", lat: 6.0535, lon: 80.2210, popularity: 70 },
    { city: "Nuwara Eliya", lat: 6.9497, lon: 80.7891, popularity: 55 },
    { city: "Mirissa", lat: 5.9483, lon: 80.4589, popularity: 45 },
    { city: "Yala National Park", lat: 6.3728, lon: 81.5183, popularity: 42 },
    { city: "Polonnaruwa", lat: 7.9403, lon: 81.0188, popularity: 38 },
    { city: "Anuradhapura", lat: 8.3114, lon: 80.4037, popularity: 38 },
  ],
  "Cambodia": [
    { city: "Siem Reap", lat: 13.3633, lon: 103.8564, popularity: 100 },
    { city: "Angkor", lat: 13.4125, lon: 103.8670, popularity: 95 },
    { city: "Phnom Penh", lat: 11.5564, lon: 104.9282, popularity: 75 },
    { city: "Sihanoukville", lat: 10.6104, lon: 103.5300, popularity: 55 },
    { city: "Battambang", lat: 13.0957, lon: 103.2022, popularity: 40 },
    { city: "Kampot", lat: 10.6104, lon: 104.1811, popularity: 38 },
    { city: "Kep", lat: 10.4826, lon: 104.3167, popularity: 32 },
  ],
  "Maldives": [{ city: "Malé", lat: 4.1755, lon: 73.5093, popularity: 100 }],
  "United States": [
    { city: "New York", lat: 40.7128, lon: -74.0060, popularity: 100 },
    { city: "Los Angeles", lat: 34.0522, lon: -118.2437, popularity: 88 },
    { city: "Las Vegas", lat: 36.1699, lon: -115.1398, popularity: 82 },
    { city: "San Francisco", lat: 37.7749, lon: -122.4194, popularity: 80 },
    { city: "Orlando", lat: 28.5383, lon: -81.3792, popularity: 75 },
    { city: "Chicago", lat: 41.8781, lon: -87.6298, popularity: 75 },
    { city: "Washington", lat: 38.9072, lon: -77.0369, popularity: 72 },
    { city: "Miami", lat: 25.7617, lon: -80.1918, popularity: 72 },
    { city: "Boston", lat: 42.3601, lon: -71.0589, popularity: 65 },
    { city: "Honolulu", lat: 21.3069, lon: -157.8583, popularity: 65 },
    { city: "San Diego", lat: 32.7157, lon: -117.1611, popularity: 58 },
    { city: "New Orleans", lat: 29.9511, lon: -90.0715, popularity: 58 },
    { city: "Seattle", lat: 47.6062, lon: -122.3321, popularity: 55 },
    { city: "Houston", lat: 29.7604, lon: -95.3698, popularity: 48 },
    { city: "Dallas", lat: 32.7767, lon: -96.7970, popularity: 42 },
    { city: "Atlanta", lat: 33.7490, lon: -84.3880, popularity: 42 },
    { city: "Philadelphia", lat: 39.9526, lon: -75.1652, popularity: 40 },
  ],
  "Mexico": [
    { city: "Cancun", lat: 21.1619, lon: -86.8515, popularity: 100 },
    { city: "Mexico City", lat: 19.4326, lon: -99.1332, popularity: 95 },
    { city: "Tulum", lat: 20.2114, lon: -87.4654, popularity: 80 },
    { city: "Playa del Carmen", lat: 20.6296, lon: -87.0739, popularity: 75 },
    { city: "Cabo San Lucas", lat: 22.8905, lon: -109.9167, popularity: 70 },
    { city: "Puerto Vallarta", lat: 20.6534, lon: -105.2253, popularity: 68 },
    { city: "Isla Cozumel", lat: 20.4230, lon: -86.9223, popularity: 60 },
    { city: "Guadalajara", lat: 20.6597, lon: -103.3496, popularity: 55 },
    { city: "Oaxaca", lat: 17.0732, lon: -96.7266, popularity: 55 },
    { city: "San Miguel de Allende", lat: 20.9153, lon: -100.7439, popularity: 52 },
    { city: "Puebla", lat: 19.0414, lon: -98.2063, popularity: 45 },
    { city: "Merida", lat: 20.9674, lon: -89.5926, popularity: 45 },
    { city: "Zacatecas", lat: 22.7709, lon: -102.5832, popularity: 38 },
  ],
  "Canada": [
    { city: "Toronto", lat: 43.6532, lon: -79.3832, popularity: 100 },
    { city: "Vancouver", lat: 49.2827, lon: -123.1207, popularity: 92 },
    { city: "Montreal", lat: 45.5019, lon: -73.5674, popularity: 90 },
    { city: "Quebec City", lat: 46.8139, lon: -71.2080, popularity: 65 },
    { city: "Banff", lat: 51.1784, lon: -115.5708, popularity: 62 },
    { city: "Niagara Falls", lat: 43.0896, lon: -79.0849, popularity: 60 },
    { city: "Ottawa", lat: 45.4215, lon: -75.6972, popularity: 58 },
    { city: "Victoria", lat: 48.4284, lon: -123.3656, popularity: 48 },
    { city: "Calgary", lat: 51.0447, lon: -114.0719, popularity: 45 },
    { city: "Halifax", lat: 44.6488, lon: -63.5752, popularity: 40 },
    { city: "Edmonton", lat: 53.5461, lon: -113.4938, popularity: 38 },
  ],
  "Argentina": [
    { city: "Buenos Aires", lat: -34.6037, lon: -58.3816, popularity: 100 },
    { city: "Puerto Iguazu", lat: -25.5952, lon: -54.5738, popularity: 70 },
    { city: "Bariloche", lat: -41.1335, lon: -71.3103, popularity: 70 },
    { city: "Mendoza", lat: -32.8908, lon: -68.8272, popularity: 62 },
    { city: "Ushuaia", lat: -54.8019, lon: -68.3030, popularity: 58 },
    { city: "Salta", lat: -24.7859, lon: -65.4117, popularity: 48 },
    { city: "Cordoba", lat: -31.4201, lon: -64.1888, popularity: 45 },
    { city: "Mar del Plata", lat: -38.0055, lon: -57.5426, popularity: 42 },
    { city: "Puerto Madryn", lat: -42.7692, lon: -65.0385, popularity: 40 },
    { city: "Rosario", lat: -32.9468, lon: -60.6393, popularity: 35 },
  ],
  "Brazil": [
    { city: "Rio de Janeiro", lat: -22.9068, lon: -43.1729, popularity: 100 },
    { city: "São Paulo", lat: -23.5505, lon: -46.6333, popularity: 85 },
    { city: "Foz do Iguaçu", lat: -25.5163, lon: -54.5854, popularity: 70 },
    { city: "Florianopolis", lat: -27.5954, lon: -48.5480, popularity: 62 },
    { city: "Salvador", lat: -12.9777, lon: -38.5016, popularity: 60 },
    { city: "Recife", lat: -8.0476, lon: -34.8770, popularity: 48 },
    { city: "Brasília", lat: -15.8267, lon: -47.9218, popularity: 45 },
    { city: "Ouro Preto", lat: -20.3855, lon: -43.5035, popularity: 42 },
    { city: "Belo Horizonte", lat: -19.9167, lon: -43.9345, popularity: 40 },
    { city: "Fortaleza", lat: -3.7319, lon: -38.5267, popularity: 38 },
  ],
  "Chile": [
    { city: "Santiago de Chile", lat: -33.4489, lon: -70.6693, popularity: 100 },
    { city: "Valparaiso", lat: -33.0472, lon: -71.6127, popularity: 70 },
    { city: "Puerto Varas", lat: -41.3195, lon: -72.9854, popularity: 60 },
    { city: "Vina del Mar", lat: -33.0245, lon: -71.5518, popularity: 55 },
    { city: "Punta Arenas", lat: -53.1638, lon: -70.9171, popularity: 50 },
    { city: "Antofagasta", lat: -23.6509, lon: -70.3975, popularity: 45 },
    { city: "Valdivia", lat: -39.8142, lon: -73.2459, popularity: 40 },
    { city: "La Serena", lat: -29.9027, lon: -71.2519, popularity: 38 },
    { city: "Puerto Montt", lat: -41.4693, lon: -72.9424, popularity: 35 },
  ],
  "Peru": [
    { city: "Cusco", lat: -13.5319, lon: -71.9675, popularity: 100 },
    { city: "Lima", lat: -12.0464, lon: -77.0428, popularity: 95 },
    { city: "Puno", lat: -15.8402, lon: -70.0219, popularity: 55 },
    { city: "Arequipa", lat: -16.4090, lon: -71.5375, popularity: 55 },
    { city: "Paracas", lat: -13.7000, lon: -76.2167, popularity: 48 },
    { city: "Iquitos", lat: -3.7437, lon: -73.2516, popularity: 45 },
    { city: "Huaraz", lat: -9.5277, lon: -77.5278, popularity: 40 },
    { city: "Trujillo", lat: -8.1116, lon: -79.0287, popularity: 40 },
  ],
  "Colombia": [
    { city: "Cartagena", lat: 10.3910, lon: -75.4794, popularity: 100 },
    { city: "Medellin", lat: 6.2442, lon: -75.5812, popularity: 85 },
    { city: "Bogota", lat: 4.7110, lon: -74.0721, popularity: 85 },
    { city: "Santa Marta", lat: 11.2408, lon: -74.1990, popularity: 55 },
    { city: "Salento", lat: 4.6361, lon: -75.5709, popularity: 50 },
    { city: "San Andres", lat: 12.5847, lon: -81.7006, popularity: 48 },
    { city: "Manizales", lat: 5.0689, lon: -75.5174, popularity: 38 },
  ],
  "Ecuador": [
    { city: "Quito", lat: -0.1807, lon: -78.4678, popularity: 100 },
    { city: "Galápagos Islands", lat: -0.7393, lon: -90.3130, popularity: 90 },
    { city: "Cuenca", lat: -2.9001, lon: -79.0059, popularity: 55 },
    { city: "Guayaquil", lat: -2.1894, lon: -79.8891, popularity: 55 },
  ],
  "Bolivia": [
    { city: "La Paz", lat: -16.5000, lon: -68.1500, popularity: 100 },
    { city: "Uyuni", lat: -20.4597, lon: -66.8250, popularity: 85 },
    { city: "Santa Cruz de la Sierra", lat: -17.7833, lon: -63.1821, popularity: 55 },
    { city: "Cochabamba", lat: -17.3895, lon: -66.1568, popularity: 42 },
  ],
  "Costa Rica": [
    { city: "San José", lat: 9.9281, lon: -84.0907, popularity: 90 },
    { city: "La Fortuna", lat: 10.4713, lon: -84.6432, popularity: 80 },
    { city: "Manuel Antonio", lat: 9.3925, lon: -84.1424, popularity: 75 },
    { city: "Monteverde", lat: 10.3010, lon: -84.8226, popularity: 65 },
    { city: "Tamarindo", lat: 10.2993, lon: -85.8371, popularity: 55 },
  ],
  "Panama": [
    { city: "Panama City", lat: 8.9824, lon: -79.5199, popularity: 100 },
    { city: "Bocas del Toro", lat: 9.3400, lon: -82.2400, popularity: 65 },
    { city: "San Blas Islands", lat: 9.5667, lon: -78.9333, popularity: 50 },
  ],
  "Belize": [
    { city: "San Pedro", lat: 17.9186, lon: -87.9611, popularity: 100 },
    { city: "Belize City", lat: 17.5046, lon: -88.1962, popularity: 70 },
    { city: "Caye Caulker", lat: 17.7414, lon: -88.0247, popularity: 60 },
  ],
  "Guatemala": [
    { city: "Antigua", lat: 14.5586, lon: -90.7295, popularity: 100 },
    { city: "Lake Atitlán", lat: 14.6910, lon: -91.1900, popularity: 80 },
    { city: "Guatemala City", lat: 14.6349, lon: -90.5069, popularity: 70 },
    { city: "Tikal", lat: 17.2220, lon: -89.6237, popularity: 65 },
  ],
  "Nicaragua": [
    { city: "Granada", lat: 11.9344, lon: -85.9560, popularity: 90 },
    { city: "San Juan del Sur", lat: 11.2529, lon: -85.8703, popularity: 70 },
    { city: "Managua", lat: 12.1364, lon: -86.2514, popularity: 60 },
    { city: "Ometepe", lat: 11.5000, lon: -85.5833, popularity: 55 },
  ],
  "El Salvador": [
    { city: "San Salvador", lat: 13.6929, lon: -89.2182, popularity: 90 },
    { city: "El Tunco", lat: 13.4936, lon: -89.3789, popularity: 65 },
    { city: "Santa Ana", lat: 13.9942, lon: -89.5597, popularity: 45 },
  ],
  "Honduras": [
    { city: "Roatán", lat: 16.3249, lon: -86.5335, popularity: 100 },
    { city: "Tegucigalpa", lat: 14.0723, lon: -87.1921, popularity: 55 },
    { city: "Copán", lat: 14.8397, lon: -89.1428, popularity: 50 },
  ],
  "Turks and Caicos Islands": [{ city: "Providenciales", lat: 21.7947, lon: -72.2650, popularity: 100 }],
  "British Virgin Islands": [{ city: "Road Town", lat: 18.4292, lon: -64.6180, popularity: 100 }],
  "Saint Lucia": [
    { city: "Soufrière", lat: 13.8567, lon: -61.0564, popularity: 100 },
    { city: "Castries", lat: 14.0101, lon: -60.9875, popularity: 70 },
  ],
  "Barbados": [{ city: "Bridgetown", lat: 13.0969, lon: -59.6145, popularity: 100 }],
  "Cayman Islands": [
    { city: "George Town", lat: 19.3133, lon: -81.2546, popularity: 100 },
    { city: "Seven Mile Beach", lat: 19.3417, lon: -81.3856, popularity: 90 },
    { city: "West Bay", lat: 19.3667, lon: -81.4167, popularity: 55 },
  ],
  "Jamaica": [
    { city: "Montego Bay", lat: 18.4762, lon: -77.8939, popularity: 100 },
    { city: "Negril", lat: 18.2687, lon: -78.3475, popularity: 80 },
    { city: "Ocho Rios", lat: 18.4075, lon: -77.1032, popularity: 75 },
    { city: "Kingston", lat: 17.9714, lon: -76.7931, popularity: 65 },
  ],
  "Bahamas": [
    { city: "Nassau", lat: 25.0343, lon: -77.3963, popularity: 100 },
    { city: "Exuma", lat: 23.5167, lon: -75.8333, popularity: 60 },
    { city: "Freeport", lat: 26.5333, lon: -78.7000, popularity: 55 },
  ],
  "Aruba": [{ city: "Oranjestad", lat: 12.5211, lon: -70.0270, popularity: 100 }],
  "Dominican Republic": [
    { city: "Punta Cana", lat: 18.5601, lon: -68.3725, popularity: 100 },
    { city: "Santo Domingo", lat: 18.4861, lon: -69.9312, popularity: 75 },
    { city: "Puerto Plata", lat: 19.7934, lon: -70.6884, popularity: 55 },
    { city: "La Romana", lat: 18.4273, lon: -68.9728, popularity: 50 },
  ],
  "Cuba": [
    { city: "Havana", lat: 23.1136, lon: -82.3666, popularity: 100 },
    { city: "Varadero", lat: 23.1394, lon: -81.2764, popularity: 80 },
    { city: "Trinidad", lat: 21.8040, lon: -79.9843, popularity: 65 },
    { city: "Santiago de Cuba", lat: 20.0247, lon: -75.8219, popularity: 55 },
  ],
  "Thailand": [
    { city: "Bangkok", lat: 13.7563, lon: 100.5018, popularity: 100 },
    { city: "Phuket", lat: 7.8804, lon: 98.3923, popularity: 88 },
    { city: "Chiang Mai", lat: 18.7883, lon: 98.9853, popularity: 80 },
    { city: "Krabi", lat: 8.0863, lon: 98.9063, popularity: 75 },
    { city: "Pattaya", lat: 12.9236, lon: 100.8825, popularity: 65 },
    { city: "Hua Hin", lat: 12.5684, lon: 99.9577, popularity: 55 },
    { city: "Ayutthaya", lat: 14.3532, lon: 100.5689, popularity: 55 },
    { city: "Chiang Rai", lat: 19.9105, lon: 99.8406, popularity: 50 },
    { city: "Kanchanaburi", lat: 14.0227, lon: 99.5328, popularity: 42 },
    { city: "Sukhothai", lat: 17.0068, lon: 99.8265, popularity: 40 },
  ],
  "Uganda": [
    { city: "Kampala", lat: 0.3476, lon: 32.5825, popularity: 90 },
    { city: "Bwindi Impenetrable National Park", lat: -1.0654, lon: 29.6285, popularity: 85 },
    { city: "Murchison Falls National Park", lat: 2.2667, lon: 31.7833, popularity: 70 },
    { city: "Queen Elizabeth National Park", lat: -0.2000, lon: 29.9000, popularity: 68 },
    { city: "Jinja", lat: 0.4478, lon: 33.2026, popularity: 60 },
    { city: "Entebbe", lat: 0.0512, lon: 32.4637, popularity: 55 },
    { city: "Ssese Islands", lat: -0.3833, lon: 32.4500, popularity: 45 },
    { city: "Fort Portal", lat: 0.6710, lon: 30.2748, popularity: 40 },
  ],
  "South Africa": [
    { city: "Cape Town", lat: -33.9249, lon: 18.4241, popularity: 100 },
    { city: "Johannesburg", lat: -26.2041, lon: 28.0473, popularity: 75 },
    { city: "Durban", lat: -29.8587, lon: 31.0218, popularity: 60 },
    { city: "Stellenbosch", lat: -33.9321, lon: 18.8602, popularity: 55 },
    { city: "Knysna", lat: -34.0363, lon: 23.0471, popularity: 50 },
    { city: "Plettenberg Bay", lat: -34.0527, lon: 23.3716, popularity: 45 },
    { city: "Pretoria", lat: -25.7479, lon: 28.2293, popularity: 45 },
    { city: "Port Elizabeth", lat: -33.9608, lon: 25.6022, popularity: 42 },
    { city: "Oudtshoorn", lat: -33.5906, lon: 22.2014, popularity: 38 },
  ],
  "Cape Verde": [
    { city: "Santa Maria", lat: 16.5999, lon: -22.9037, popularity: 100 },
    { city: "Praia", lat: 14.9330, lon: -23.5133, popularity: 65 },
    { city: "Mindelo", lat: 16.8901, lon: -24.9805, popularity: 55 },
    { city: "Palmeira", lat: 16.7500, lon: -22.9833, popularity: 40 },
  ],
  "Tunisia": [
    { city: "Tunis", lat: 36.8065, lon: 10.1815, popularity: 90 },
    { city: "Hammamet", lat: 36.4000, lon: 10.6167, popularity: 85 },
    { city: "Sousse", lat: 35.8256, lon: 10.6369, popularity: 75 },
    { city: "Djerba", lat: 33.8076, lon: 10.8451, popularity: 70 },
    { city: "Carthage", lat: 36.8528, lon: 10.3233, popularity: 55 },
    { city: "Kairouan", lat: 35.6781, lon: 10.0963, popularity: 45 },
    { city: "Sfax", lat: 34.7406, lon: 10.7603, popularity: 40 },
  ],
  "Morocco": [
    { city: "Marrakech", lat: 31.6295, lon: -7.9811, popularity: 100 },
    { city: "Fes", lat: 34.0331, lon: -5.0003, popularity: 75 },
    { city: "Chefchaouen", lat: 35.1688, lon: -5.2636, popularity: 70 },
    { city: "Casablanca", lat: 33.5731, lon: -7.5898, popularity: 68 },
    { city: "Essaouira", lat: 31.5085, lon: -9.7595, popularity: 60 },
    { city: "Merzouga", lat: 31.0801, lon: -4.0133, popularity: 55 },
    { city: "Agadir", lat: 30.4278, lon: -9.5981, popularity: 55 },
    { city: "Tangiers", lat: 35.7595, lon: -5.8340, popularity: 52 },
    { city: "Rabat", lat: 34.0209, lon: -6.8416, popularity: 45 },
    { city: "Ouarzazate", lat: 30.9335, lon: -6.9370, popularity: 40 },
  ],
  "Seychelles": [
    { city: "Mahé", lat: -4.6796, lon: 55.4920, popularity: 100 },
    { city: "Praslin Island", lat: -4.3167, lon: 55.7333, popularity: 75 },
    { city: "La Digue Island", lat: -4.3500, lon: 55.8333, popularity: 65 },
    { city: "Victoria", lat: -4.6191, lon: 55.4513, popularity: 50 },
  ],
  "Mauritius": [
    { city: "Grand Baie", lat: -20.0186, lon: 57.5807, popularity: 90 },
    { city: "Port Louis", lat: -20.1609, lon: 57.5012, popularity: 70 },
    { city: "Flic en Flac", lat: -20.2726, lon: 57.3644, popularity: 65 },
    { city: "Mahebourg", lat: -20.4081, lon: 57.7000, popularity: 45 },
    { city: "Curepipe", lat: -20.3155, lon: 57.5233, popularity: 38 },
  ],
  "Kenya": [
    { city: "Maasai Mara", lat: -1.4061, lon: 35.0117, popularity: 95 },
    { city: "Nairobi", lat: -1.2921, lon: 36.8219, popularity: 90 },
    { city: "Mombasa", lat: -4.0435, lon: 39.6682, popularity: 75 },
    { city: "Malindi", lat: -3.2192, lon: 40.1169, popularity: 55 },
    { city: "Lamu", lat: -2.2696, lon: 40.9020, popularity: 50 },
    { city: "Naivasha", lat: -0.7167, lon: 36.4333, popularity: 42 },
    { city: "Nakuru", lat: -0.3031, lon: 36.0800, popularity: 40 },
  ],
  "Ethiopia": [
    { city: "Addis Ababa", lat: 9.0320, lon: 38.7469, popularity: 90 },
    { city: "Lalibela", lat: 12.0317, lon: 39.0473, popularity: 90 },
    { city: "Axum", lat: 14.1213, lon: 38.7269, popularity: 65 },
    { city: "Gondar", lat: 12.6030, lon: 37.4521, popularity: 60 },
    { city: "Bahir Dar", lat: 11.5936, lon: 37.3908, popularity: 55 },
    { city: "Harar", lat: 9.3111, lon: 42.1181, popularity: 45 },
    { city: "Mekelle", lat: 13.4967, lon: 39.4753, popularity: 35 },
  ],
  "Mozambique": [
    { city: "Maputo", lat: -25.9692, lon: 32.5732, popularity: 85 },
    { city: "Bazaruto National Park", lat: -21.6500, lon: 35.4500, popularity: 75 },
    { city: "Quirimbas Archipelago", lat: -12.3833, lon: 40.6333, popularity: 65 },
    { city: "Inhaca Islands", lat: -26.0333, lon: 32.9167, popularity: 50 },
    { city: "Nampula", lat: -15.1165, lon: 39.2666, popularity: 40 },
  ],
  "Zambia": [
    { city: "Victoria Falls", lat: -17.9243, lon: 25.8572, popularity: 90 },
    { city: "Livingstone", lat: -17.8419, lon: 25.8543, popularity: 75 },
    { city: "South Luangwa National Park", lat: -13.0928, lon: 31.7581, popularity: 60 },
    { city: "Lusaka", lat: -15.3875, lon: 28.3228, popularity: 55 },
    { city: "Lower Zambezi National Park", lat: -15.7000, lon: 29.4667, popularity: 50 },
  ],
  "Botswana": [
    { city: "Okavango Delta", lat: -19.2833, lon: 22.9833, popularity: 100 },
    { city: "Chobe National Park", lat: -18.6528, lon: 24.7136, popularity: 85 },
    { city: "Moremi Game Reserve", lat: -19.1833, lon: 23.4000, popularity: 70 },
    { city: "Maun", lat: -19.9833, lon: 23.4167, popularity: 55 },
    { city: "Central Kalahari Game Reserve", lat: -21.5000, lon: 24.0000, popularity: 50 },
    { city: "Gaborone", lat: -24.6282, lon: 25.9231, popularity: 40 },
  ],
  "Namibia": [
    { city: "Sossusvlei", lat: -24.7333, lon: 15.3000, popularity: 95 },
    { city: "Etosha National Park", lat: -18.8927, lon: 16.4022, popularity: 90 },
    { city: "Swakopmund", lat: -22.6783, lon: 14.5267, popularity: 75 },
    { city: "Windhoek", lat: -22.5609, lon: 17.0658, popularity: 60 },
    { city: "Walvis Bay", lat: -22.9575, lon: 14.5053, popularity: 45 },
    { city: "Fish River Canyon Park", lat: -27.5833, lon: 17.6167, popularity: 45 },
  ],
  "Egypt": [
    { city: "Cairo", lat: 30.0444, lon: 31.2357, popularity: 100 },
    { city: "Giza", lat: 29.9765, lon: 31.1313, popularity: 85 },
    { city: "Luxor", lat: 25.6872, lon: 32.6396, popularity: 85 },
    { city: "Sharm el Sheikh", lat: 27.9158, lon: 34.3300, popularity: 78 },
    { city: "Hurghada", lat: 27.2579, lon: 33.8116, popularity: 70 },
    { city: "Aswan", lat: 24.0889, lon: 32.8998, popularity: 60 },
    { city: "Alexandria", lat: 31.2001, lon: 29.9187, popularity: 58 },
    { city: "Dahab", lat: 28.5091, lon: 34.5136, popularity: 48 },
    { city: "Siwa Oasis", lat: 29.2032, lon: 25.5195, popularity: 40 },
  ],
  "Tanzania": [
    { city: "Serengeti", lat: -2.3333, lon: 34.8333, popularity: 100 },
    { city: "Ngorongoro", lat: -3.2000, lon: 35.5000, popularity: 85 },
    { city: "Zanzibar", lat: -6.1659, lon: 39.2026, popularity: 95 },
    { city: "Arusha", lat: -3.3869, lon: 36.6830, popularity: 65 },
    { city: "Moshi", lat: -3.3349, lon: 37.3400, popularity: 55 },
    { city: "Dar es Salaam", lat: -6.7924, lon: 39.2083, popularity: 55 },
  ],
  "Algeria": [{ city: "Algiers", lat: 36.7538, lon: 3.0588, popularity: 100 }],
  "Angola": [{ city: "Luanda", lat: -8.8390, lon: 13.2894, popularity: 100 }],
  "Benin": [{ city: "Cotonou", lat: 6.3703, lon: 2.3912, popularity: 100 }],
  "Burkina Faso": [{ city: "Ouagadougou", lat: 12.3714, lon: -1.5197, popularity: 100 }],
  "Burundi": [{ city: "Bujumbura", lat: -3.3822, lon: 29.3644, popularity: 100 }],
  "Cameroon": [
    { city: "Douala", lat: 4.0511, lon: 9.7679, popularity: 100 },
    { city: "Yaoundé", lat: 3.8480, lon: 11.5021, popularity: 85 },
  ],
  "Central African Republic": [{ city: "Bangui", lat: 4.3947, lon: 18.5582, popularity: 100 }],
  "Chad": [{ city: "N'Djamena", lat: 12.1348, lon: 15.0557, popularity: 100 }],
  "Côte d'Ivoire": [
    { city: "Abidjan", lat: 5.3600, lon: -4.0083, popularity: 100 },
    { city: "Bouaké", lat: 7.6906, lon: -5.0303, popularity: 50 },
  ],
  "Congo": [
    { city: "Kinshasa", lat: -4.4419, lon: 15.2663, popularity: 100 },
    { city: "Lubumbashi", lat: -11.6876, lon: 27.5026, popularity: 45 },
  ],
  "Republic of Congo": [
    { city: "Brazzaville", lat: -4.2634, lon: 15.2429, popularity: 100 },
    { city: "Pointe-Noire", lat: -4.7889, lon: 11.8656, popularity: 55 },
  ],
  "Eritrea": [{ city: "Asmara", lat: 15.3229, lon: 38.9251, popularity: 100 }],
  "Gabon": [{ city: "Libreville", lat: 0.4162, lon: 9.4673, popularity: 100 }],
  "Ghana": [
    { city: "Accra", lat: 5.6037, lon: -0.1870, popularity: 100 },
    { city: "Kumasi", lat: 6.6885, lon: -1.6244, popularity: 55 },
  ],
  "Guinea": [{ city: "Conakry", lat: 9.6412, lon: -13.5784, popularity: 100 }],
  "Liberia": [{ city: "Monrovia", lat: 6.2907, lon: -10.7605, popularity: 100 }],
  "Libya": [
    { city: "Tripoli", lat: 32.8872, lon: 13.1913, popularity: 100 },
    { city: "Benghazi", lat: 32.1167, lon: 20.0667, popularity: 55 },
  ],
  "Madagascar": [{ city: "Antananarivo", lat: -18.8792, lon: 47.5079, popularity: 100 }],
  "Malawi": [
    { city: "Lilongwe", lat: -13.9626, lon: 33.7741, popularity: 90 },
    { city: "Blantyre", lat: -15.7861, lon: 35.0058, popularity: 60 },
  ],
  "Mali": [{ city: "Bamako", lat: 12.6392, lon: -8.0029, popularity: 100 }],
  "Mauritania": [{ city: "Nouakchott", lat: 18.0735, lon: -15.9582, popularity: 100 }],
  "Niger": [{ city: "Niamey", lat: 13.5127, lon: 2.1128, popularity: 100 }],
  "Nigeria": [
    { city: "Lagos", lat: 6.5244, lon: 3.3792, popularity: 100 },
    { city: "Abuja", lat: 9.0765, lon: 7.3986, popularity: 60 },
    { city: "Kano", lat: 12.0022, lon: 8.5920, popularity: 40 },
  ],
  "Rwanda": [{ city: "Kigali", lat: -1.9403, lon: 29.8739, popularity: 100 }],
  "Senegal": [{ city: "Dakar", lat: 14.7167, lon: -17.4677, popularity: 100 }],
  "Sierra Leone": [{ city: "Freetown", lat: 8.4657, lon: -13.2317, popularity: 100 }],
  "Somalia": [{ city: "Mogadishu", lat: 2.0469, lon: 45.3182, popularity: 100 }],
  "Sudan": [{ city: "Khartoum", lat: 15.5007, lon: 32.5599, popularity: 100 }],
  "Togo": [{ city: "Lomé", lat: 6.1319, lon: 1.2228, popularity: 100 }],
  "Zimbabwe": [
    { city: "Victoria Falls", lat: -17.9318, lon: 25.8305, popularity: 100 },
    { city: "Harare", lat: -17.8252, lon: 31.0335, popularity: 60 },
  ],
};
